// OEM App 定制 — UI 设计确认：mock 数据与状态工具
// 说明：UI 设计为「整项目一份」（多张图片 + 文字说明），保留版本历史
// 状态由最后一个版本的结果推导：none / pending / confirmed / rejected
// 状态文案按端区分：企业端（text）/ 管理端（serviceText）
// TODO: 联调时替换为接口数据

// ── 状态定义（text=企业端口径，serviceText=管理端口径）──
export const UI_STATUS = {
    none: { text: '待平台上传', serviceText: '待上传', color: 'default' },
    pending: { text: '待我确认', serviceText: '待客户确认', color: 'processing' },
    confirmed: { text: '我已确认', serviceText: '客户已确认', color: 'success' },
    rejected: { text: '我已驳回', serviceText: '客户已驳回', color: 'error' },
};

// 按端取状态文案（isService=true → 管理端）
export const uiStatusLabel = (status, isService) => {
    const s = UI_STATUS[status] || UI_STATUS.none;
    return isService ? s.serviceText : s.text;
};

// 状态提示语（client=企业端口径，service=管理端口径）
export const UI_STATUS_TIP = {
    none: { client: '待平台上传 UI 设计', service: '尚未上传 UI 设计' },
    pending: { client: '请确认平台上传的 UI 设计', service: '已上传，等待客户确认，确认通过后方可构建一阶段' },
    confirmed: { client: '我已确认 UI 设计，平台可构建一阶段', service: '客户已确认 UI 设计，可构建一阶段' },
    rejected: { client: '我已驳回，等待平台修改后重新上传', service: '客户已驳回，请按理由修改后重新上传' },
};

export const uiStatusTip = (status, isService) => {
    const t = UI_STATUS_TIP[status] || UI_STATUS_TIP.none;
    return isService ? t.service : t.client;
};

// 时间串（mock）
const pad = (n) => String(n).padStart(2, '0');
export const nowStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 离线可用的设计稿占位图（data URI SVG），避免依赖外网图片
export const designImage = (name, version, color = '#1677ff') => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="426" viewBox="0 0 240 426">
<rect width="240" height="426" fill="#ffffff"/>
<rect width="240" height="92" fill="${color}"/>
<text x="16" y="44" font-size="17" font-family="sans-serif" fill="#ffffff">${name}</text>
<text x="16" y="68" font-size="11" font-family="sans-serif" fill="#ffffff" opacity="0.85">OEM App UI · ${version}</text>
<rect x="16" y="112" rx="8" width="208" height="96" fill="#f5f5f5"/>
<rect x="16" y="220" rx="8" width="208" height="56" fill="#fafafa"/>
<rect x="16" y="288" rx="8" width="100" height="56" fill="#fafafa"/>
<rect x="124" y="288" rx="8" width="100" height="56" fill="#fafafa"/>
<rect x="16" y="356" rx="8" width="208" height="40" fill="#f5f5f5"/>
</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const screensOf = (names, version, color) => names.map((n) => ({ name: n, url: designImage(n, version, color) }));

// 版本记录：result = pending | confirmed | rejected
const ver = (version, names, color, note, uploadedAt, uploadedBy, result, extra = {}) => ({
    version,
    screens: screensOf(names, `v${version}`, color),
    note,
    uploadedAt,
    uploadedBy,
    result,
    ...extra,
});

const MAIN_SCREENS = ['登录页', '首页', '设备列表', '个人中心'];

// 预置 UI 设计状态（示例数据，覆盖 none / pending / confirmed / rejected 四种场景）
export const INITIAL_UI_STATES = {
    // none：未上传（一阶段资料未满）→ 企业端「待平台上传」/ 管理端「待上传」
    'OEM-1001': { versions: [] },
    // 已确认（一阶段构建中）
    'OEM-1002': {
        versions: [ver(1, MAIN_SCREENS, '#1677ff', '首版设计，覆盖登录/首页/设备/我的四个主页面', '2026-08-25 16:40', '平台UI', 'confirmed', { confirmedAt: '2026-08-26 10:05' })],
    },
    // pending：v1 已驳回 → v2 待确认（一阶段资料已满但 UI 未确认 → 构建置灰）
    'OEM-1003': {
        versions: [
            ver(1, ['登录页', '首页', '我的'], '#1677ff', '首版设计', '2026-08-18 15:20', '平台UI', 'rejected', { rejectReason: '整体风格偏暗，主色与品牌色不一致，首页布局需按图调整', confirmedAt: '2026-08-19 11:30' }),
            ver(2, MAIN_SCREENS, '#13c2c2', '按客户意见调整：主色改为品牌青、首页信息层级重排', '2026-08-20 09:50', '平台UI', 'pending'),
        ],
    },
    'OEM-1004': { versions: [] },
    // 已确认（一阶段构建失败）
    'OEM-1005': {
        versions: [ver(1, ['登录页', '首页', '商城'], '#722ed1', '海外版首版设计', '2026-07-29 14:10', '平台UI', 'confirmed', { confirmedAt: '2026-07-30 09:40' })],
    },
    // 已驳回（等待平台修改）
    'OEM-1006': {
        versions: [ver(1, ['登录页', '首页', '我的'], '#fa8c16', '首版设计', '2026-09-07 17:30', '平台UI', 'rejected', { rejectReason: '底部导航图标与现网 App 不一致，需替换为定制图标', confirmedAt: '2026-09-08 10:20' })],
    },
    // 已确认
    'OEM-1007': {
        versions: [ver(1, MAIN_SCREENS, '#52c41a', '国内+海外双区域首版设计（含中英文界面）', '2026-09-09 11:15', '平台UI', 'confirmed', { confirmedAt: '2026-09-10 09:30' })],
    },
    // 已确认
    'OEM-1008': {
        versions: [ver(1, MAIN_SCREENS, '#eb2f96', '首版设计（含中英文界面）', '2026-09-10 16:00', '平台UI', 'confirmed', { confirmedAt: '2026-09-11 09:00' })],
    },
    // 已确认（一阶段已构建、待创建应用）
    'OEM-1009': {
        versions: [ver(1, ['登录页', '首页', '我的'], '#2f54eb', '首版设计（国内版）', '2026-09-11 14:20', '平台UI', 'confirmed', { confirmedAt: '2026-09-12 09:10' })],
    },
};

export const EMPTY_UI_STATE = { versions: [] };

export const getUiState = (map, id) => map?.[id] || EMPTY_UI_STATE;

// 由版本历史推导当前状态
export const uiStatusOf = (state) => {
    const versions = state?.versions || [];
    if (!versions.length) return 'none';
    const last = versions[versions.length - 1];
    if (last.result === 'confirmed') return 'confirmed';
    if (last.result === 'rejected') return 'rejected';
    return 'pending';
};

export const uiStatusInfo = (state) => UI_STATUS[uiStatusOf(state)];

// 当前（最新）版本
export const currentVersion = (state) => {
    const versions = state?.versions || [];
    return versions.length ? versions[versions.length - 1] : null;
};
